import { defineStore } from "pinia";
import { getPowerConsumeTypes } from "@/@api/PowerApi";
import { getPowerConsumeRules, type PowerConsumeRule } from "@/@api/StoryboardApi";

/** 并发去重：类型枚举拉取中 */
let typesPromise: Promise<void> | null = null;
/** 并发去重：扣费规则拉取中 */
let rulesPromise: Promise<void> | null = null;

/**
 * 影豆业务类型枚举 + 扣费规则缓存。
 * 登录后由路由守卫 init；计费与流水展示统一读本 store。
 */
export const usePowerStore = defineStore("power", {
  state: () => ({
    /** code → 中文名（来自 /ai-video/power/consume-types） */
    bizTypeLabelMap: {} as Record<number, string>,
    /** 扣费规则列表（来自 /ai-video/power/consume-rules） */
    consumeRules: [] as PowerConsumeRule[],
    typesLoaded: false,
    rulesLoaded: false,
  }),
  getters: {
    /** 根据 bizType 返回展示名；未加载或未知 code 时回退为数字字符串 */
    getBizTypeLabel:
      (state) =>
      (bizType?: number): string => {
        if (bizType == null) return "-";
        return state.bizTypeLabelMap[bizType] || String(bizType);
      },
    /** 根据中文业务名反查 code；未加载或未命中返回 undefined */
    getBizTypeCodeByName:
      (state) =>
      (name?: string): number | undefined => {
        if (!name) return undefined;
        for (const [code, label] of Object.entries(state.bizTypeLabelMap)) {
          if (label === name) return Number(code);
        }
        return undefined;
      },
  },
  actions: {
    /** 幂等拉取业务类型 + 扣费规则；失败不抛，保持空数据 */
    async init(): Promise<void> {
      await Promise.all([this.loadConsumeTypes(), this.loadConsumeRules()]);
    },

    /** 幂等拉取 consume-types */
    async loadConsumeTypes(): Promise<void> {
      if (this.typesLoaded) return;
      if (typesPromise) return typesPromise;
      typesPromise = (async () => {
        try {
          const list = await getPowerConsumeTypes();
          const next: Record<number, string> = {};
          for (const item of list || []) {
            if (item != null && typeof item.code === "number") {
              next[item.code] = item.name;
            }
          }
          this.bizTypeLabelMap = next;
          this.typesLoaded = true;
        } catch {
          // 拉取失败保持空映射，展示侧回退为数字；下次 init 可重试
        } finally {
          typesPromise = null;
        }
      })();
      return typesPromise;
    },

    /**
     * 拉取 consume-rules。
     * @param force 为 true 时忽略已加载缓存，强制重新请求（管理端改规则后同步用）
     */
    async loadConsumeRules(force = false): Promise<void> {
      if (!force && this.rulesLoaded) return;
      if (rulesPromise) return rulesPromise;
      rulesPromise = (async () => {
        try {
          this.consumeRules = (await getPowerConsumeRules()) || [];
          this.rulesLoaded = true;
        } catch {
          this.consumeRules = [];
          this.rulesLoaded = true;
        } finally {
          rulesPromise = null;
        }
      })();
      return rulesPromise;
    },
  },
});
